import type { PhaseZeroDto, PhaseZeroSectionDto, WizardStepDto } from '@bk/contracts';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, errorMessage } from '../../lib/api';
import { useTenant, useTenantQuery } from '../../lib/useTenant';
import { Neurons, clock, Level, spent, Timeline, useNow } from './Sinapsi';
import { PhaseCommandBar } from '../tenant/wizard/Comandi';
import { PhaseFooter, Questions, Talk } from './Fase';

/**
 * La fase 0 «Conosco l'azienda» (docs/mockup/percorso-sinapsi/03-fase0; contratto n. 1 versione 2.0, fase 0, art. 7-bis
 * e 9.3): il profilo in tre righe, l'atto sezione per sezione con i bloccanti e il «perché?», i conti in tre gradini, le
 * domande con le risposte chiuse e «Basta, decidi tu», il livello con il passo che manca, «Non va bene» e «Avanti».
 * Ogni dato arriva dal server: la pagina non inventa nulla.
 */
export function Fase0Page({ next, onNext, onWithout, sealing, stopped = false }: { next: WizardStepDto | null; onNext: () => void; /** Senza la visura si va avanti con i dati online, da confermare nel passo «L'impresa». */ onWithout: () => void; sealing: boolean; /** Il titolare ha fermato la fase dai Comandi (ADR 0172): si può andare avanti con ciò che c'è. */ stopped?: boolean }) {
  const { t, tenant } = useTenant();
  const qc = useQueryClient();
  const q = useTenantQuery<PhaseZeroDto>(['fase0'], '/onboarding/fase0', {
    refetchInterval: (d) => (!d || d.stato === 'in_preparazione' || d.profilo.stato === 'in_preparazione' || (d.tempi && !d.tempi.fine) ? 3000 : false),
  });
  const set = (d: PhaseZeroDto) => qc.setQueryData(['tenant', tenant.id, 'fase0'], d);
  const redo = useMutation({
    mutationFn: (motivo: string) => api<PhaseZeroDto>(t('/onboarding/fase0/rifai'), { method: 'POST', body: { motivo } }),
    onSuccess: (d) => {
      set(d);
      void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] });
    },
  });
  const d = q.data;
  if (!d) return q.error ? <p className="sn-err">{errorMessage(q.error)}</p> : <p className="sn-note">Apro la fase 0…</p>;
  const open = d.domande.filter((x) => x.stato === 'aperta');
  const done = d.tempi?.fine && d.tempi.conteggio_da ? clock(spent(d.tempi.conteggio_da, d.tempi.fine, 0)) : null;

  if (d.stato !== 'pronta')
    return (
      <div className="sn-main">
        {d.tempi ? (
          <Timeline t={d.tempi} eyebrow="Il consulente al lavoro" title="Conosco la tua azienda" />
        ) : (
          <section className="sn-panel">
            <p className="sn-ey cyan">Fase 0</p>
            <h1 className="sn-h1 sm">Conosco la tua azienda</h1>
            <p className="sn-note">{d.stato === 'senza_visura' ? 'La visura non è arrivata dal Registro Imprese: vado avanti con i dati verificati online e nei registri pubblici, con la loro fonte.' : 'Scarico e leggo la visura camerale: la pagina si aggiorna da sola.'}</p>
          </section>
        )}
        {stopped && d.stato !== 'senza_visura' && (
          <p className="sn-note">Hai fermato la fase: puoi andare avanti con i dati trovati finora, o riprenderla dai Comandi.</p>
        )}
        {(d.stato === 'senza_visura' || stopped) && next && (
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button type="button" className="sn-btn sm" disabled={sealing} onClick={onWithout}>
              Avanti: {next.titolo.toLowerCase()}
            </button>
          </div>
        )}
        {/* I comandi del titolare mentre aspetta (ADR 0172): ferma la fase, torna indietro, esci dal contratto. */}
        <PhaseCommandBar />
        <Neurons tenantId={tenant.id} />
      </div>
    );

  return (
    <main className="sn-main" id="f0">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        <p className="sn-ey cyan">Fase 0{done ? ` · Fatta in ${done}` : d.tempi && !d.tempi.fine ? ' · Al lavoro' : ''}</p>
        <h1 className="sn-h1" style={{ fontSize: 30, fontWeight: 700, lineHeight: 1.15 }}>
          Conosco la tua azienda
        </h1>
        {d.livello && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Level value={d.livello.valore} />
            <span style={{ fontSize: 14, color: '#c3cbe0' }}>
              <b style={{ color: '#eaf0ff' }}>
                Livello {d.livello.valore} su 5, {d.livello.nome}.
              </b>{' '}
              {d.livello.prossimo ?? ''} {d.livello.nota ?? ''}
            </span>
          </div>
        )}
      </div>

      {d.tempi && !d.tempi.fine && <Timeline t={d.tempi} eyebrow="Il consulente al lavoro" title="Completo la fase 0" />}

      {d.cambiato.length > 0 && (
        <div className="sn-why" style={{ fontSize: 13 }}>
          <b>Cosa è cambiato.</b> {d.cambiato.join(' ')}
        </div>
      )}

      <section className="sn-panel" style={{ gap: 10 }}>
        <p className="sn-ey cyan" style={{ fontSize: 11 }}>
          Il profilo · scritto dal consulente
        </p>
        {d.profilo.stato === 'pronto' ? (
          <dl className="sn-dl wide">
            <dt>Settore</dt>
            <dd>{d.profilo.settore ?? 'Da confermare.'}</dd>
            <dt>Dimensione</dt>
            <dd>{d.profilo.dimensione ?? 'Da confermare.'}</dd>
            <dt>Territorio</dt>
            <dd>{d.profilo.territorio ?? 'Da confermare.'}</dd>
          </dl>
        ) : (
          <p className="sn-note">
            <span className="sn-dot spin" style={{ verticalAlign: -1, marginRight: 8 }} />
            Il consulente scrive il profilo dall’atto, dai conti e dalle pagine verificate…
          </p>
        )}
        {d.profilo.non_torna.length > 0 && (
          <div className="sn-why gold">
            <b>Non torna tra le fonti.</b> {d.profilo.non_torna.join(' ')}
          </div>
        )}
      </section>

      <section style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h2 className="sn-h2">L’atto dell’impresa</h2>
          <span className="sn-ey" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
            {d.fonte} · {d.grado}
          </span>
        </div>
        <div className="sn-pills">
          {d.bloccanti.map((b) => (
            <span key={b.cosa} className={`sn-pill ${b.ok ? '' : 'no'}`}>
              {b.cosa} {b.dettaglio}
            </span>
          ))}
        </div>
        <div className="sn-acc">
          {d.sezioni.map((s, k) => (
            <Section key={s.chiave} s={s} open={k === 0} />
          ))}
        </div>
      </section>

      <section className="sn-panel">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
          <h2 className="sn-h2">I conti</h2>
          <span className="sn-ey" style={{ fontSize: 11, letterSpacing: '0.06em' }}>
            Bilancio in tre gradini
          </span>
        </div>
        <div className="sn-steps3">
          {d.conti.gradini.map((g) => (
            <span key={g.n} style={{ display: 'contents' }}>
              <span className={`sn-dot ${g.stato === 'da_confermare' ? 'gold' : g.stato === 'in_corso' ? 'spin' : g.stato === 'saltato' ? 'off' : 'ok'}`} />
              <span>
                <b>
                  {g.n}. {g.titolo}
                </b>{' '}
                <span className={g.stato === 'da_confermare' ? 'sn-gold' : 'sn-soft'}>{g.testo}</span>
              </span>
            </span>
          ))}
        </div>
      </section>

      <Questions
        domande={d.domande}
        limiti={d.limiti}
        intro="Solo ciò che nessuna fonte dice. Se non rispondi, uso la risposta accesa e la segno come scelta di Timonio; la fase resta al livello 4."
        onChange={() => void qc.invalidateQueries({ queryKey: ['tenant', tenant.id] })}
      />

      <Talk fase={0} />

      <PhaseFooter
        rifatta={d.rifatta}
        confermata={d.confermata}
        canConfirm
        onRedo={(motivo) => redo.mutateAsync(motivo)}
        onConfirm={() => api<PhaseZeroDto>(t('/onboarding/fase0/conferma'), { method: 'POST' }).then(set)}
        next={next}
        onNext={onNext}
        sealing={sealing}
      />
      <p className="sn-note">
        {open.length ? `${open.length} ${open.length === 1 ? 'domanda aperta' : 'domande aperte'}: puoi andare avanti lo stesso, niente aspetta le tue risposte.` : 'Nessuna domanda aperta.'}
      </p>
    </main>
  );
}

/** Una sezione dell'atto: i dati leggibili, il «perché?» con la citazione, la contraddizione se c'è. */
function Section({ s, open }: { s: PhaseZeroSectionDto; open: boolean }) {
  const [more, setMore] = useState<Record<number, boolean>>({});
  return (
    <details open={open}>
      <summary>
        {s.titolo}
        <span className={`n ${s.tono === 'attenzione' ? 'gold' : ''}`}>{s.nota}</span>
      </summary>
      <dl className="sn-dl">
        {s.voci.map((v, k) => {
          const long = v.valore.length > 280;
          const shown = long && !more[k] ? `${v.valore.slice(0, 260).trim()}…` : v.valore;
          const mono = /^[\d\s./-]+$/.test(v.valore) || /partita iva|codice fiscale/i.test(v.campo);
          return (
            <span key={k} style={{ display: 'contents' }}>
              <dt>{v.campo}</dt>
              <dd className={mono ? 'mono' : ''} title={`Citazione: «${v.citazione}»`}>
                {v.da_confermare ? (
                  <>
                    {shown.replace(/\s*·?\s*indirizzo da confermare/i, '')} · indirizzo <span className="sn-gold">da confermare</span>
                  </>
                ) : (
                  shown
                )}
                {long && (
                  <>
                    {' '}
                    <button type="button" className="sn-link" onClick={() => setMore((m) => ({ ...m, [k]: !m[k] }))}>
                      {more[k] ? 'meno' : '· leggi tutto'}
                    </button>
                  </>
                )}
              </dd>
            </span>
          );
        })}
      </dl>
      {s.contraddizione && (
        <p className="sn-why gold">
          <b>Contraddizione.</b> {s.contraddizione}
        </p>
      )}
      {s.perche && (
        <p className="sn-why">
          <span className="q">Perché?</span> {s.perche}
        </p>
      )}
    </details>
  );
}
